// ChatWidget.jsx
//
// "Chat with BIBA" as a floating bot on every page: a launcher bubble in the
// bottom-right corner that opens a chat window with the customer's one
// support conversation with BIBA staff (Tixaru is the backend, see
// src/api/chat.js). A message can be about one of their orders, and anything
// that needs tracking can be raised as a ticket with its own number; the
// conversation about that ticket carries on in the same window. Polled every
// few seconds while the window is open (Tixaru has no push channel for the
// website) and every 30s for the unread badge while it is closed.
//
// Anything else can open it with openChat({ orderId }) (My Orders / order
// detail "Message us" buttons, the Account page link, and the /my-chat route
// that BIBA's reply emails link to).
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FaPaperclip, FaTimes } from "react-icons/fa";
import { onAuthStateChanged } from "firebase/auth";
import { collection, query, where, orderBy, limit, getDocs } from "firebase/firestore";
import { auth, db } from "../../firebase";
import {
  listMessages,
  listTickets,
  sendMessage,
  raiseTicket,
  markRead,
  getUnreadCount,
  uploadAttachment,
  fetchAttachmentBlobUrl,
  TICKET_CATEGORIES,
  TICKET_STATUS_LABELS,
} from "../../api/chat";
import { OPEN_CHAT_EVENT } from "./openChat";
import "./ChatWidget.css";

const POLL_INTERVAL_MS = 4000;
const UNREAD_INTERVAL_MS = 30000;
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
const EMPTY_TICKET_FORM = { subject: "", description: "", category: TICKET_CATEGORIES[0].value, orderId: "" };

const formatDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
};

const formatFileSize = (bytes) => {
  const n = Number(bytes);
  if (!n || isNaN(n)) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const statusSuffix = (status) => String(status || "open").toLowerCase().replace(/_/g, "-");

const ChatIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 11.5a8.4 8.4 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.4 8.4 0 01-3.8-.9L3 21l1.9-5.7a8.4 8.4 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.4 8.4 0 013.8-.9h.5a8.5 8.5 0 018 8v.5z" />
  </svg>
);

const ChatWidget = () => {
  const navigate = useNavigate();
  const [authUser, setAuthUser] = useState(null);
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  const [messages, setMessages] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [orders, setOrders] = useState([]); // [{ id, label }]
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState("");

  // "" = general chat, otherwise a ticket id
  const [targetTicketId, setTargetTicketId] = useState("");
  const [orderId, setOrderId] = useState("");
  const [body, setBody] = useState("");
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");

  const [view, setView] = useState("chat"); // "chat" | "ticket"
  const [form, setForm] = useState(EMPTY_TICKET_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const fileInputRef = useRef(null);
  const endRef = useRef(null);
  const lastCountRef = useRef(0);
  const lastMarkedRef = useRef(null);
  const ordersLoadedFor = useRef(null);

  useEffect(() => onAuthStateChanged(auth, (u) => setAuthUser(u || null)), []);

  // Signing out wipes the conversation from memory.
  useEffect(() => {
    if (authUser) return;
    setMessages([]);
    setTickets([]);
    setOrders([]);
    setLoaded(false);
    setUnread(0);
    setTargetTicketId("");
    lastMarkedRef.current = null;
    lastCountRef.current = 0;
    ordersLoadedFor.current = null;
  }, [authUser]);

  useEffect(() => {
    const onOpen = (e) => {
      const id = e && e.detail && e.detail.orderId;
      if (id) setOrderId(id);
      setView("chat");
      setOpen(true);
    };
    window.addEventListener(OPEN_CHAT_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_CHAT_EVENT, onOpen);
  }, []);

  // Unread badge on the launcher while the window is closed. A failure just
  // leaves the badge as it was (it is a convenience, not data).
  useEffect(() => {
    if (!authUser || open) return undefined;
    let cancelled = false;
    const check = async () => {
      if (document.visibilityState !== "visible") return;
      try {
        const data = await getUnreadCount();
        if (!cancelled) setUnread(Number(data && data.unread) || 0);
      } catch (err) {
        // Leave the badge as it was.
      }
    };
    check();
    const timer = setInterval(check, UNREAD_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [authUser, open]);

  // Their own orders, for the "about which order?" picker -- the same
  // uid-scoped query My Orders runs. Fetched the first time the window opens.
  useEffect(() => {
    if (!authUser || !open || ordersLoadedFor.current === authUser.uid) return;
    ordersLoadedFor.current = authUser.uid;
    (async () => {
      try {
        const snap = await getDocs(
          query(collection(db, "orders"), where("uid", "==", authUser.uid), orderBy("createdAt", "desc"), limit(30))
        );
        setOrders(
          snap.docs.map((d) => ({
            id: d.id,
            label: d.data().orderNumber || `#${d.id.slice(-8).toUpperCase()}`,
          }))
        );
      } catch (err) {
        console.error("Load orders for chat error:", err);
        ordersLoadedFor.current = null;
      }
    })();
  }, [authUser, open]);

  const load = useCallback(async () => {
    try {
      const [messagesData, ticketsData] = await Promise.all([listMessages(), listTickets()]);
      setMessages(Array.isArray(messagesData) ? messagesData : []);
      setTickets(Array.isArray(ticketsData) ? ticketsData : []);
      setLoadError("");
      setLoaded(true);
    } catch (err) {
      console.error("Load chat error:", err);
      // Only surface the error before anything has loaded; a failed poll
      // shouldn't blank an open conversation.
      setLoadError((prev) => prev || err.message || "Could not load your chat. Please try again.");
    }
  }, []);

  // Fetch now, then poll while the window is open and the tab is visible.
  useEffect(() => {
    if (!authUser || !open) return undefined;
    load();
    let timer = null;
    const start = () => {
      if (!timer) timer = setInterval(load, POLL_INTERVAL_MS);
    };
    const stop = () => {
      if (timer) clearInterval(timer);
      timer = null;
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        load();
        start();
      } else {
        stop();
      }
    };
    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [authUser, open, load]);

  useEffect(() => {
    if (open && view === "chat" && messages.length !== lastCountRef.current) {
      lastCountRef.current = messages.length;
      endRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, open, view]);

  // Having the window open marks BIBA's messages as read (once per newest one).
  useEffect(() => {
    if (!open) return;
    const lastStaff = [...messages].reverse().find((m) => m.sender_type === "staff");
    if (!lastStaff || lastStaff.id === lastMarkedRef.current) return;
    if (document.visibilityState !== "visible") return;
    lastMarkedRef.current = lastStaff.id;
    setUnread(0);
    markRead().catch(() => {
      lastMarkedRef.current = null;
    });
  }, [messages, open]);

  const openTickets = tickets.filter((t) => t.status === "open" || t.status === "in_progress");
  const targetTicket = tickets.find((t) => t.id === targetTicketId) || null;

  const handleFileChange = (e) => {
    const picked = e.target.files?.[0] || null;
    setSendError("");
    if (picked && picked.size > MAX_ATTACHMENT_BYTES) {
      setSendError("That file is larger than 10MB. Please choose a smaller file.");
      e.target.value = "";
      setFile(null);
      return;
    }
    setFile(picked);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    const text = body.trim();
    if ((!text && !file) || sending) return;
    setSending(true);
    setSendError("");
    try {
      const message = await sendMessage({
        body: text,
        ticketId: targetTicket ? targetTicket.id : undefined,
        orderId: targetTicket ? undefined : orderId || undefined,
        hasAttachment: !!file,
      });
      if (file) await uploadAttachment(message.id, file);
      setBody("");
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      await load();
    } catch (err) {
      console.error("Send chat message error:", err);
      setSendError(err.message || "Could not send your message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const openTicketForm = () => {
    setForm({ ...EMPTY_TICKET_FORM, orderId });
    setSubmitError("");
    setView("ticket");
  };

  const handleRaiseTicket = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.description.trim()) {
      setSubmitError("Please fill in both a subject and a description.");
      return;
    }
    setSubmitting(true);
    setSubmitError("");
    try {
      const created = await raiseTicket({
        subject: form.subject.trim(),
        description: form.description.trim(),
        category: form.category,
        orderId: form.orderId || undefined,
      });
      setView("chat");
      // Carry on talking about it right here.
      if (created && created.id) setTargetTicketId(created.id);
      await load();
    } catch (err) {
      console.error("Raise ticket error:", err);
      setSubmitError(err.message || "Could not raise the ticket. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewAttachment = async (attachment) => {
    try {
      const blobUrl = await fetchAttachmentBlobUrl(attachment.id);
      window.open(blobUrl, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
    } catch (err) {
      alert(err.message || "Could not open this file. Please try again.");
    }
  };

  const renderMessage = (m) => {
    if (m.kind === "ticket") {
      return (
        <div key={m.id} className="cw-ticket-card">
          <div className="cw-ticket-card-head">
            <strong>Ticket #{m.ticket_number}</strong>
            {m.ticket_status && (
              <span className={`cw-status cw-status-${statusSuffix(m.ticket_status)}`}>
                {TICKET_STATUS_LABELS[m.ticket_status] || m.ticket_status}
              </span>
            )}
          </div>
          <div className="cw-ticket-subject">{m.ticket_subject}</div>
          <div className="cw-ticket-body">{m.body}</div>
          {m.related_order_number && <div className="cw-tag">Order {m.related_order_number}</div>}
          <div className="cw-meta">{formatDateTime(m.created_at)}</div>
        </div>
      );
    }
    const mine = m.sender_type === "customer";
    return (
      <div key={m.id} className={`cw-row ${mine ? "mine" : "theirs"}`}>
        {(m.ticket_number || m.related_order_number) && (
          <div className="cw-tags">
            {m.ticket_number && <span className="cw-tag">Ticket #{m.ticket_number}</span>}
            {m.related_order_number && !m.ticket_number && <span className="cw-tag">Order {m.related_order_number}</span>}
          </div>
        )}
        <div className="cw-bubble">
          {m.body && <div className="cw-bubble-text">{m.body}</div>}
          {m.attachments && m.attachments.length > 0 && (
            <div className="cw-attachments">
              {m.attachments.map((a) => (
                <button key={a.id} type="button" onClick={() => handleViewAttachment(a)} title={a.original_filename}>
                  <FaPaperclip /> {a.original_filename} ({formatFileSize(a.file_size)})
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="cw-meta">
          {mine ? "You" : m.sender_name} · {formatDateTime(m.created_at)}
        </div>
      </div>
    );
  };

  return (
    <>
      {!open && (
        <button className="cw-launcher" onClick={() => setOpen(true)} aria-label="Chat with BIBA" title="Chat with BIBA">
          <ChatIcon />
          {unread > 0 && <span className="cw-launcher-badge">{unread > 9 ? "9+" : unread}</span>}
        </button>
      )}

      {open && (
        <section className="cw-window" role="dialog" aria-label="Chat with BIBA">
          <header className="cw-header">
            <div className="cw-header-title">
              <strong>Chat with BIBA</strong>
              <span>We usually reply within working hours</span>
            </div>
            {authUser && view === "chat" && (
              <button className="cw-header-btn" onClick={openTicketForm}>
                Raise a ticket
              </button>
            )}
            <button className="cw-close" onClick={() => setOpen(false)} aria-label="Close chat">
              <FaTimes />
            </button>
          </header>

          {!authUser ? (
            <div className="cw-signin">
              <p>Hi! Sign in to your BIBA account to message our team, ask about an order or raise a ticket.</p>
              <button
                className="cw-primary"
                onClick={() => {
                  setOpen(false);
                  navigate("/login");
                }}
              >
                Sign in
              </button>
            </div>
          ) : view === "ticket" ? (
            <form className="cw-ticket-form" onSubmit={handleRaiseTicket}>
              <p>A ticket gets its own number so we can track it until it is sorted. You can keep chatting about it here.</p>
              <label>
                Subject
                <input value={form.subject} maxLength={200} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} />
              </label>
              <label>
                What happened?
                <textarea rows={4} value={form.description} maxLength={5000} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
              </label>
              <label>
                Category
                <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}>
                  {TICKET_CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Related order (optional)
                <select value={form.orderId} onChange={(e) => setForm((f) => ({ ...f, orderId: e.target.value }))}>
                  <option value="">None</option>
                  {orders.map((o) => (
                    <option key={o.id} value={o.id}>
                      Order {o.label}
                    </option>
                  ))}
                </select>
              </label>
              {submitError && <p className="cw-error">{submitError}</p>}
              <div className="cw-form-actions">
                <button type="button" className="cw-secondary" onClick={() => setView("chat")} disabled={submitting}>
                  Back
                </button>
                <button type="submit" className="cw-primary" disabled={submitting}>
                  {submitting ? "Raising..." : "Raise ticket"}
                </button>
              </div>
            </form>
          ) : (
            <>
              <div className="cw-thread">
                {loadError && !loaded && <p className="cw-error">{loadError}</p>}
                {loaded && messages.length === 0 && (
                  <p className="cw-empty">Hi! Ask us anything, or about one of your orders. We will reply right here.</p>
                )}
                {!loaded && !loadError && <p className="cw-empty">Loading...</p>}
                {messages.map(renderMessage)}
                <div ref={endRef} />
              </div>

              <form className="cw-composer" onSubmit={handleSend}>
                <div className="cw-options">
                  <select
                    aria-label="Send to"
                    value={targetTicket ? targetTicket.id : ""}
                    onChange={(e) => setTargetTicketId(e.target.value)}
                  >
                    <option value="">General chat</option>
                    {openTickets.map((t) => (
                      <option key={t.id} value={t.id}>
                        Ticket #{t.ticket_number} - {t.subject}
                      </option>
                    ))}
                    {targetTicket && !openTickets.some((t) => t.id === targetTicket.id) && (
                      <option value={targetTicket.id}>
                        Ticket #{targetTicket.ticket_number} - {targetTicket.subject}
                      </option>
                    )}
                  </select>
                  {!targetTicket && (
                    <select aria-label="About which order" value={orderId} onChange={(e) => setOrderId(e.target.value)}>
                      <option value="">No particular order</option>
                      {orders.map((o) => (
                        <option key={o.id} value={o.id}>
                          Order {o.label}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                {file && (
                  <div className="cw-file-chip">
                    <FaPaperclip /> {file.name}
                    <button type="button" onClick={() => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}>
                      Remove
                    </button>
                  </div>
                )}
                <div className="cw-composer-row">
                  <textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSend(e);
                      }
                    }}
                    placeholder="Type your message..."
                    rows={2}
                    maxLength={5000}
                  />
                  <button type="button" className="cw-attach" onClick={() => fileInputRef.current?.click()} disabled={sending} title="Attach a file">
                    <FaPaperclip />
                  </button>
                  <input ref={fileInputRef} type="file" accept="image/*,application/pdf" style={{ display: "none" }} onChange={handleFileChange} />
                  <button type="submit" className="cw-primary" disabled={sending || (!body.trim() && !file)}>
                    {sending ? "..." : "Send"}
                  </button>
                </div>
                {sendError && <p className="cw-error">{sendError}</p>}
              </form>
            </>
          )}
        </section>
      )}
    </>
  );
};

export default ChatWidget;
